import React from "react";
import { Paper, Box, Typography, useTheme, alpha, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const MostActiveTopicsChart = ({ data = [], loading = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLight = theme.palette.mode === "light";

  // Enhanced glassmorphism background matching StatsCard
  const glassBg = isLight ? "rgba(255,255,255,0.90)" : "rgba(34,45,59,0.85)";

  // Dynamic shadow matching StatsCard
  const boxShadow = isLight
    ? "0 4px 24px 0 rgba(2,89,165,0.10), 0 1.5px 8px 0 rgba(0,0,0,0.03)"
    : "0 4px 24px 0 rgba(0,188,212,0.07), 0 1.5px 8px 0 rgba(0,0,0,0.25)";

  // Border color matching StatsCard
  const borderColor = isLight ? "rgba(2,89,165,0.08)" : "rgba(0,188,212,0.18)";

  // Color palette for bars - using theme primary colors
  const barColors = [
    theme.palette.primary.main,
    alpha(theme.palette.primary.main, 0.85),
    alpha(theme.palette.primary.main, 0.7),
    alpha(theme.palette.primary.main, 0.6),
    alpha(theme.palette.primary.main, 0.5),
    alpha(theme.palette.primary.main, 0.45),
    alpha(theme.palette.primary.main, 0.4),
    alpha(theme.palette.primary.main, 0.35),
    alpha(theme.palette.primary.main, 0.3),
    alpha(theme.palette.primary.main, 0.25),
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            background: isLight
              ? "rgba(255,255,255,0.98)"
              : "rgba(34,45,59,0.98)",
            backdropFilter: "blur(10px)",
            border: `1px solid ${borderColor}`,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 0.5,
            }}
          >
            {data.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: "block",
              mb: 0.5,
            }}
          >
            {data.category}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
            }}
          >
            {data.value} {t("dashboard.contributionsCount")}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Format chart data - use numbers instead of topic titles
  const chartData = data.map((item, index) => {
    return {
      ...item,
      displayLabel: `${index + 1}`,
    };
  });

  // Handle bar click to navigate to topic single view
  const handleBarClick = (data) => {
    if (data && data.id) {
      navigate(`/single_topic/${data.id}`);
    }
  };

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          borderRadius: { xs: 3, md: 5 },
          background: glassBg,
          boxShadow: boxShadow,
          border: `1px solid ${borderColor}`,
          backdropFilter: "blur(12px)",
          minHeight: { xs: 400, sm: 450, md: 500 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: "0.8rem", md: "0.875rem" },
          }}
        >
          {t("dashboard.loadingData")}
        </Typography>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          borderRadius: { xs: 3, md: 5 },
          background: glassBg,
          boxShadow: boxShadow,
          border: `1px solid ${borderColor}`,
          backdropFilter: "blur(12px)",
          minHeight: { xs: 400, sm: 450, md: 500 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: "0.8rem", md: "0.875rem" },
          }}
        >
          {t("topics.noTopicsFound")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: { xs: 3, md: 5 },
        background: glassBg,
        boxShadow: boxShadow,
        border: `1px solid ${borderColor}`,
        backdropFilter: "blur(12px)",
        height: "100%",
        minHeight: { xs: 400, sm: 450, md: 500 },
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: isLight
            ? "0 12px 48px 0 rgba(2,89,165,0.15), 0 4px 20px 0 rgba(0,0,0,0.08)"
            : "0 12px 48px 0 rgba(0,188,212,0.18), 0 4px 20px 0 rgba(0,0,0,0.35)",
          transform: "translateY(-4px)",
          borderColor: isLight
            ? alpha(theme.palette.primary.main, 0.2)
            : alpha(theme.palette.primary.main, 0.3),
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: { xs: 2, md: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1.5, sm: 0 },
          minHeight: 64,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, md: 1.5 },
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.15,
              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              borderRadius: 2.5,
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            <TrendingUpIcon
              sx={{
                color: theme.palette.primary.main,
                fontSize: { xs: 24, md: 28 },
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.15rem", md: "1.3rem" },
                color: theme.palette.text.primary,
                letterSpacing: 0.1,
              }}
            >
              {t("dashboard.mostActiveTopics")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: "0.75rem", md: "0.85rem" },
              }}
            >
              {t("dashboard.topTopicsByContributions", { count: data.length })}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={t("dashboard.topicsLabel", { count: data.length })}
          size="small"
          sx={{
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.15,
            )} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
            color: theme.palette.primary.main,
            fontWeight: 600,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            fontSize: { xs: "0.75rem", md: "0.8rem" },
          }}
        />
      </Box>

      {/* Spacer to match Top Contributors legend height */}
      <Box sx={{ height: 42, mb: 2 }} />

      {/* Chart */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 250, sm: 300, md: 300 },
          mt: { xs: 1, md: 2 },
          flex: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 10,
              right: 20,
              left: -20,
              bottom: -30,
            }}
            barCategoryGap="20%"
            onClick={handleBarClick}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={isLight ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.05)"}
              vertical={false}
            />
            <XAxis
              dataKey="label"
              angle={0}
              textAnchor="middle"
              height={60}
              interval={0}
              tick={(props) => {
                const { x, y, payload } = props;
                const maxLength = 12; // Maximum characters to show
                const text = payload.value;
                const truncatedText =
                  text.length > maxLength
                    ? text.substring(0, maxLength) + "..."
                    : text;
                return (
                  <text
                    x={x}
                    y={y + 10}
                    fill={theme.palette.text.secondary}
                    fontSize={10}
                    fontWeight={500}
                    textAnchor="middle"
                    style={{ cursor: "pointer" }}
                  >
                    <title>{text}</title>
                    {truncatedText}
                  </text>
                );
              }}
              stroke={isLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
              tickLine={false}
              onClick={handleBarClick}
              style={{ cursor: "pointer" }}
            />
            <YAxis
              tick={{
                fill: theme.palette.text.secondary,
                fontSize: 11,
              }}
              stroke={isLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"}
              allowDecimals={false}
            />
            <RechartsTooltip
              content={<CustomTooltip />}
              cursor={{
                fill: isLight
                  ? alpha(theme.palette.primary.main, 0.05)
                  : alpha(theme.palette.primary.main, 0.08),
                cursor: "pointer",
              }}
            />
            <Bar
              dataKey="value"
              radius={[8, 8, 0, 0]}
              maxBarSize={60}
              animationDuration={800}
              animationEasing="ease-out"
              onClick={handleBarClick}
              cursor="pointer"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={barColors[index % barColors.length]}
                  style={{
                    filter: `drop-shadow(0px 2px 4px ${alpha(
                      barColors[index % barColors.length],
                      0.3,
                    )})`,
                    cursor: "pointer",
                  }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Legend - Category breakdown */}
      {/* {data.length > 0 && (
        <Box
          sx={{
            // mt: 1,
            pt: 1.5,
            borderTop: `1px solid ${
              isLight ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)"
            }`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: "block",
              mb: 1,
              fontWeight: 500,
            }}
          >
            Categories:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {[...new Set(data.map((item) => item.category))].map(
              (category, index) => (
                <Chip
                  key={index}
                  label={category}
                  size="small"
                  sx={{
                    background: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.text.primary,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                  }}
                />
              )
            )}
          </Box>
        </Box>
      )} */}
    </Paper>
  );
};

export default MostActiveTopicsChart;
